var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import React, { useEffect, useRef, useState } from 'react';
import store from 'store';
import { Dialog, Button, toast, FileInput } from 'day8-ui';
import { useEditProfilePictureModal } from '../../../context/EditProfilePictureModalContext';
import { useDispatch, useSelector } from 'react-redux';
var CROP_SIZE = 260; // px, square crop viewport
var MIN_ZOOM = 1;
var MAX_ZOOM = 3;
var OUTPUT_SIZE = 1000; // px, fixed output size for the saved profile picture
var OUTPUT_QUALITY = 0.85;
var getDisplaySize = function (image, zoom) {
    var baseScale = CROP_SIZE / Math.min(image.naturalWidth, image.naturalHeight);
    var scale = baseScale * zoom;
    return { width: image.naturalWidth * scale, height: image.naturalHeight * scale, scale: scale };
};
var clampOffset = function (image, zoom, offset) {
    var _a = getDisplaySize(image, zoom), width = _a.width, height = _a.height;
    var minX = Math.min(0, CROP_SIZE - width);
    var minY = Math.min(0, CROP_SIZE - height);
    return {
        x: Math.min(0, Math.max(minX, offset.x)),
        y: Math.min(0, Math.max(minY, offset.y))
    };
};
var EditProfilePictureModal = function () {
    var dispatch = useDispatch();
    var user = useSelector(function (state) { return state.user.loggedUser; });
    var _a = useEditProfilePictureModal(), isModalOpen = _a.isModalOpen, close = _a.close, modalProps = _a.modalProps;
    var _b = useState(null), selectedFile = _b[0], setSelectedFile = _b[1];
    var _c = useState(null), cropImage = _c[0], setCropImage = _c[1];
    var _d = useState(MIN_ZOOM), zoom = _d[0], setZoom = _d[1];
    var _e = useState({ x: 0, y: 0 }), offset = _e[0], setOffset = _e[1];
    var _f = useState(false), isUploading = _f[0], setIsUploading = _f[1];
    var _g = useState(null), fileError = _g[0], setFileError = _g[1];
    var objectUrlRef = useRef(null);
    var dragStateRef = useRef(null);
    useEffect(function () {
        return function () {
            if (objectUrlRef.current) {
                URL.revokeObjectURL(objectUrlRef.current);
            }
        };
    }, []);
    var resetCropState = function () {
        if (objectUrlRef.current) {
            URL.revokeObjectURL(objectUrlRef.current);
            objectUrlRef.current = null;
        }
        setSelectedFile(null);
        setCropImage(null);
        setZoom(MIN_ZOOM);
        setOffset({ x: 0, y: 0 });
    };
    var handleFileSelect = function (event) {
        var _a;
        var file = (_a = event.target.files) === null || _a === void 0 ? void 0 : _a[0];
        setFileError(null);
        if (file) {
            // Validate file size (15MB limit)
            var maxSize = 15 * 1024 * 1024; // 15MB
            if (file.size > maxSize) {
                setFileError('File size must be less than 15MB');
                toast.error('File size must be less than 15MB');
                return;
            }
            // Validate file type
            var allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
            if (!allowedTypes.includes(file.type)) {
                setFileError('Only JPEG, PNG and GIF images are allowed');
                toast.error('Only JPEG, PNG and GIF images are allowed');
                return;
            }
            // Validate image dimensions and load it for cropping
            var objectUrl_1 = URL.createObjectURL(file);
            var img_1 = new Image();
            img_1.onload = function () {
                var maxWidth = 6000;
                var maxHeight = 6000;
                if (img_1.width > maxWidth || img_1.height > maxHeight) {
                    setFileError("Image dimensions must be at most ".concat(maxWidth, "x").concat(maxHeight, " pixels. Your image is ").concat(img_1.width, "x").concat(img_1.height, " pixels."));
                    toast.error("Image too large. Maximum dimensions: ".concat(maxWidth, "x").concat(maxHeight, "px"));
                    URL.revokeObjectURL(objectUrl_1);
                    // Reset file input
                    if (event.target) {
                        event.target.value = '';
                    }
                }
                else {
                    objectUrlRef.current = objectUrl_1;
                    setSelectedFile(file);
                    setCropImage(img_1);
                    var _a = getDisplaySize(img_1, MIN_ZOOM), width = _a.width, height = _a.height;
                    setZoom(MIN_ZOOM);
                    setOffset({ x: (CROP_SIZE - width) / 2, y: (CROP_SIZE - height) / 2 });
                }
            };
            img_1.onerror = function () {
                setFileError('Unable to read image dimensions');
                toast.error('Invalid image file');
                URL.revokeObjectURL(objectUrl_1);
            };
            img_1.src = objectUrl_1;
        }
        else {
            resetCropState();
        }
    };
    var handlePointerDown = function (event) {
        if (!cropImage)
            return;
        event.currentTarget.setPointerCapture(event.pointerId);
        dragStateRef.current = { startX: event.clientX, startY: event.clientY, startOffset: offset };
    };
    var handlePointerMove = function (event) {
        if (!cropImage || !dragStateRef.current)
            return;
        var _a = dragStateRef.current, startX = _a.startX, startY = _a.startY, startOffset = _a.startOffset;
        var next = {
            x: startOffset.x + (event.clientX - startX),
            y: startOffset.y + (event.clientY - startY)
        };
        setOffset(clampOffset(cropImage, zoom, next));
    };
    var handlePointerUp = function () {
        dragStateRef.current = null;
    };
    var handleZoomChange = function (event) {
        if (!cropImage)
            return;
        var newZoom = parseFloat(event.target.value);
        setZoom(newZoom);
        setOffset(function (prev) { return clampOffset(cropImage, newZoom, prev); });
    };
    var getCroppedBlob = function () {
        return new Promise(function (resolve, reject) {
            if (!cropImage) {
                reject(new Error('No image to crop'));
                return;
            }
            var scale = getDisplaySize(cropImage, zoom).scale;
            var sourceX = -offset.x / scale;
            var sourceY = -offset.y / scale;
            var sourceSize = CROP_SIZE / scale;
            var canvas = document.createElement('canvas');
            canvas.width = OUTPUT_SIZE;
            canvas.height = OUTPUT_SIZE;
            var ctx = canvas.getContext('2d');
            if (!ctx) {
                reject(new Error('Canvas is not supported'));
                return;
            }
            // Flatten onto white first: JPEG has no alpha channel, so a transparent PNG source
            // would otherwise render with a black background.
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
            ctx.drawImage(cropImage, sourceX, sourceY, sourceSize, sourceSize, 0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
            canvas.toBlob(function (blob) { return (blob ? resolve(blob) : reject(new Error('Failed to generate cropped image'))); }, 'image/jpeg', OUTPUT_QUALITY);
        });
    };
    var handleUpload = function () { return __awaiter(void 0, void 0, void 0, function () {
        var croppedBlob, croppedFile, formData, response, data, userWithNewProfilePicture, error_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!selectedFile || !cropImage)
                        return [2 /*return*/];
                    setIsUploading(true);
                    toast.info('Uploading profile picture...');
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 5, 6, 7]);
                    return [4 /*yield*/, getCroppedBlob()];
                case 2:
                    croppedBlob = _a.sent();
                    croppedFile = new File([croppedBlob], 'profile-picture.jpg', { type: croppedBlob.type });
                    formData = new FormData();
                    formData.append('profilePicture', croppedFile);
                    return [4 /*yield*/, fetch('/api/account/profile/picture', {
                            method: 'POST',
                            body: formData,
                            headers: {
                                'X-Requested-With': 'XMLHttpRequest'
                            }
                        })];
                case 3:
                    response = _a.sent();
                    return [4 /*yield*/, response.json()];
                case 4:
                    data = _a.sent();
                    if (data.status === 'success') {
                        // Call success callback if provided
                        if (modalProps === null || modalProps === void 0 ? void 0 : modalProps.onSuccess) {
                            modalProps.onSuccess(data.data.profilePicture);
                        }
                        userWithNewProfilePicture = __assign(__assign({}, user), { profilePicture: data.data.profilePicture });
                        dispatch({
                            type: 'SAVE_USER',
                            user: userWithNewProfilePicture
                        });
                        // Global navbar user icon:
                        localStorage.user = JSON.stringify(userWithNewProfilePicture);
                        store.set('user', userWithNewProfilePicture);
                        // Close modal and reset state
                        handleClose();
                        toast.success('Profile picture uploaded successfully.');
                    }
                    else {
                        toast.error(data.message || 'Upload failed. Please try again.');
                    }
                    return [3 /*break*/, 7];
                case 5:
                    error_1 = _a.sent();
                    console.error('Upload error:', error_1);
                    toast.error('Upload failed. Please try again.');
                    return [3 /*break*/, 7];
                case 6:
                    setIsUploading(false);
                    return [7 /*endfinally*/];
                case 7: return [2 /*return*/];
            }
        });
    }); };
    var handleClose = function () {
        resetCropState();
        setFileError(null);
        close();
    };
    return (React.createElement(Dialog, { open: isModalOpen, onOpenChange: function (open) { return !open && handleClose(); }, scrollable: true },
        React.createElement("div", { className: "dui-p-6 dui-max-w-md dui-mx-auto" },
            React.createElement("h2", { className: "dui-text-2xl dui-font-bold dui-mb-4 dui-text-center" }, "Edit Profile Picture"),
            React.createElement("div", { className: "dui-space-y-4" },
                !cropImage && (React.createElement(FileInput, { name: "profilePicture", accept: "image/jpeg,image/jpg,image/png,image/gif", onChange: handleFileSelect, label: "Profile Picture", description: "Supported formats: JPEG, PNG, GIF. Max dimensions: 6000x6000px", previewSize: "LARGE", dropZoneText: "Click to Upload", maxFileSizeText: "Max. File Size: 15MB", errorMessage: fileError || undefined })),
                cropImage && (React.createElement("div", null,
                    React.createElement("div", { className: "dui-relative dui-mx-auto dui-overflow-hidden dui-rounded-lg dui-border dui-border-gray-300 dui-bg-gray-100", style: { width: CROP_SIZE, height: CROP_SIZE, cursor: 'grab', touchAction: 'none' }, onPointerDown: handlePointerDown, onPointerMove: handlePointerMove, onPointerUp: handlePointerUp, onPointerLeave: handlePointerUp },
                        React.createElement("img", { src: cropImage.src, alt: "Crop preview", draggable: false, style: {
                                position: 'absolute',
                                left: offset.x,
                                top: offset.y,
                                width: getDisplaySize(cropImage, zoom).width,
                                height: getDisplaySize(cropImage, zoom).height,
                                maxWidth: 'none',
                                userSelect: 'none'
                            } })),
                    React.createElement("div", { className: "dui-flex dui-items-center dui-space-x-2 dui-mt-3" },
                        React.createElement("span", { className: "dui-text-gray-500 dui-text-sm" }, "Zoom"),
                        React.createElement("input", { type: "range", min: MIN_ZOOM, max: MAX_ZOOM, step: 0.01, value: zoom, onChange: handleZoomChange, className: "dui-flex-1" })),
                    React.createElement("button", { type: "button", onClick: resetCropState, className: "dui-block dui-mx-auto dui-mt-2 dui-text-sm dui-text-primary-900 dui-underline", disabled: isUploading }, "Choose a different photo"))),
                React.createElement("div", { className: "dui-flex dui-space-x-3 dui-pt-4" },
                    React.createElement(Button, { buttonType: "SECONDARY", onClick: handleClose, className: "dui-flex-1", disabled: isUploading }, "Cancel"),
                    React.createElement(Button, { buttonType: "PRIMARY", onClick: handleUpload, className: "dui-flex-1", disabled: !selectedFile || isUploading }, isUploading ? 'Uploading...' : 'Upload'))))));
};
export default EditProfilePictureModal;
